import { defineStore } from "pinia";
import { reactive, ref } from "vue";

import {
  useSubmitComment,
  useSubmitReply,
} from "@/composition/api/comments.ts";
import { useMessengerAuthor } from "@/composition/messenger/useMessengerAuthor.ts";
import type { DpaNewsEnvelope } from "@/types/messenger/comments";

export const useCommentsStore = defineStore("comments", () => {
  const { messengerAuthor } = useMessengerAuthor();
  const commentEnvelopes = ref<DpaNewsEnvelope[]>([]);
  const latestUpdatedEnvelope = ref<DpaNewsEnvelope | null>(null);

  const newComment = reactive({
    valueUnitGuid: "",
    valueUnitSupplier: "",
    commentBody: "",
    commentAuthor: messengerAuthor.value,
  });

  const newReply = reactive({
    commentBody: "",
    commentId: "",
    commentAuthor: messengerAuthor.value,
  });

  const { execute: postComment, error: errorPostComment } =
    useSubmitComment(newComment);
  const { execute: postReply, error: replyError } = useSubmitReply(newReply);

  function updateEnvelope(updatedEnvelope: DpaNewsEnvelope) {
    const store = useCommentsStore();
    store.$patch((state) => {
      const index = state.commentEnvelopes.findIndex(
        (envelope) => envelope.guid === updatedEnvelope.guid,
      );

      if (index !== -1) {
        const updated = {
          ...state.commentEnvelopes[index],
          lastCommentTimestamp: updatedEnvelope.lastCommentTimestamp,
          totalMessages: updatedEnvelope.totalMessages,
          isNew: true,
        };

        state.latestUpdatedEnvelope = updated;
        state.commentEnvelopes.splice(index, 1);
        state.commentEnvelopes.unshift(updated);
      } else {
        state.latestUpdatedEnvelope = updatedEnvelope;
        state.commentEnvelopes.unshift({
          ...updatedEnvelope,
          isNew: true,
        });
      }
    });
  }

  async function handlePostComment(commentBody: string, valueUnitGuid: string) {
    newComment.commentBody = commentBody;
    newComment.valueUnitGuid = valueUnitGuid;
    newComment.commentAuthor = messengerAuthor.value;
    await postComment();
  }

  async function handlePostReply(replyBody: string, commentId: string) {
    newReply.commentBody = replyBody;
    newReply.commentId = commentId;
    newReply.commentAuthor = messengerAuthor.value;
    await postReply();
  }

  return {
    commentEnvelopes,
    latestUpdatedEnvelope,
    updateEnvelope,
    handlePostComment,
    errorPostComment,
    handlePostReply,
    replyError,
  };
});
